import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toReadableTotal, toReadableTimestamp } from '../utils/orderUtils';

import { Tooltip as ReactTooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'

import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';
import useOrderActions from '../hooks/useOrderActions';
import usePdfManagement from '../hooks/usePdfManagement';
import usePrintManagement from '../hooks/usePrintManagement';
import useOrderEditing from '../hooks/useOrderEditing';
import useRefund from '../hooks/useRefund';

import OrderSummary from '../components/OrderDetails/OrderSummary';
import OrderCost from '../components/OrderDetails/OrderCost';
import RefundInfo from '../components/OrderDetails/RefundInfo';
import OrderShipping from '../components/OrderDetails/OrderShipping';
import OrderBilling from '../components/OrderDetails/OrderBilling';
import OrderDelivery from '../components/OrderDetails/OrderDelivery';
import OrderDiscount from '../components/OrderDetails/OrderDiscount';

const GiftDetails = ({ testMode = false }) => {
  // const host = testMode ? config.testBackendUrl : config.backendUrl;
  const host = config.backendUrl;
  
  const [pdf, setPdf] = useState(null);
  const { orderId } = useParams();
  const [order, setOrder] = useState(null)
  const [luluIds, setLuluIds] = useState([])
  const [choiceLegend, setChoiceLegend] = useState({});
  const [loadingPdf, setLoadingPdf] = useState(false);

  const [tab, setTab] = useState('cost');
  const [expandedChoices, setExpandedChoices] = useState([]);
  const [authToken, setAuthToken] = useState('');

  const { terminateOrder, updateStatus, updateOrderNote } = useOrderActions(order, setOrder, authToken);
  const { refund } = useRefund({ order, authToken });

  const {
    handleEditValue,
    handleEditShippingValue,
    updateChoices,
    updateNestedValue,
  } = useOrderEditing({ order, setOrder, orderId, authToken });

  useEffect(() => {
    console.log("Host URL:", host);
    const getAuthToken = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        setAuthToken(token);
      } catch (error) {
        console.error('Error getting auth token:', error);
      }
    };
    
    getAuthToken();
  }, []);

  useEffect(() => {
    if (!authToken) return; // Don't fetch if no token yet

    fetch(`${host}/api/admin/order/${orderId}`, {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(data => {
        setOrder(data);
    })
    .catch(error => {
      console.error('Error fetching order:', error);
    });
  }, [authToken, orderId]);

  // Fetch product legends for tooltip hints once order is loaded
  useEffect(() => {
    if (!order) return;

    const uniqueProductIds = Array.from(new Set(
      (order.items || []).map(item => item.readable_id).filter(Boolean)
    ));

    uniqueProductIds.forEach((productId) => {
      if (choiceLegend[productId]) return; // already loaded

      fetch(`${host}/api/products/${productId}`, {
        method: 'GET',
        headers: {
          'X-API-KEY': process.env.REACT_APP_GANDF_KEY,
          'Content-Type': 'application/json',
        },
      })
        .then((response) => response.json())
        .then((productData) => {
          setChoiceLegend((prev) => ({
            ...prev,
            [productId]: productData?.legend || {},
          }));
        })
        .catch((error) => {
          console.error('Error fetching product legend:', error);
        });
    });
  }, [order, host]);

  const renderCell = (value, itemIndex, path = '') => {
    if (value === null || value === undefined) {
      return (
        <span
          style={{ cursor: 'pointer', color: 'var(--text-muted)', fontStyle: 'italic' }}
          onDoubleClick={() => handleEditValue(itemIndex, path, '')}
        >
          (empty)
        </span>
      );
    }

    if (Array.isArray(value)) {
      return (
        <ul style={{ margin: 0, paddingLeft: 'var(--spacing-lg)' }}>
          {value.map((item, idx) => (
            <li key={idx}>
              {renderCell(item, itemIndex, `${path}[${idx}]`)}
            </li>
          ))}
        </ul>
      );
    }

    if (typeof value === 'object') {
      return (
        <table className="table" style={{ margin: 0 }}>
          <tbody>
            {Object.keys(value).map((key, idx) => (
              <tr key={idx}>
                <td style={{ fontWeight: 'var(--font-weight-semibold)' }}>{key}</td>
                <td 
                  onDoubleClick={() => !value[key] && handleEditValue(itemIndex, path ? `${path}.${key}` : key, '')}
                  style={{ cursor: !value[key] ? 'pointer' : 'default' }}
                >
                  {renderCell(
                    value[key],
                    itemIndex,
                    path ? `${path}.${key}` : key
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    
    // For primitive values, make them editable
    return (
      <span
        style={{ cursor: 'pointer' }}
        onDoubleClick={() => handleEditValue(itemIndex, path, value)}
      >
        {value}
      </span>
    );
  };

  if (!order) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
          <div className="spinner" style={{ margin: '0 auto' }}></div>
          <p style={{ marginTop: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>
            Loading order details...
          </p>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const statusMap = {
      'purchase_pending': { class: 'badge-warning', text: 'Pending' },
      'order_placed': { class: 'badge-success', text: 'Placed' },
      'sent_to_printer': { class: 'badge-info', text: 'Sent to Printer' },
      'printed': { class: 'badge-success', text: 'Printed' },
      'awaiting_delivery': { class: 'badge-warning', text: 'Awaiting Delivery' },
      'complete': { class: 'badge-success', text: 'Complete' },
      'cancelled': { class: 'badge-error', text: 'Cancelled' },
      'refunded': { class: 'badge-secondary', text: 'Refunded' }
    };
    
    const statusInfo = statusMap[status] || { class: 'badge-secondary', text: status };
    return <span className={`badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  return (
    <div className="main-content">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-header-title">
            Order Details
            {testMode && <span style={{color: 'var(--warning-color)'}}> (Test Mode)</span>}
          </h1>
          <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
            Order ID: {order.id}
          </div>
        </div>
        <div className="page-header-actions">
          <Link to={testMode ? "/test/orders" : "/orders"} className="btn btn-secondary">
            ← Back to Orders
          </Link>
        </div>
      </div>

      {/* Order Summary Card */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <h3 className="card-title">Order Summary</h3>
        </div>
        <div className="card-body">
          <OrderSummary order={order} testMode={testMode} />
        </div>
      </div>

      {/* Order Actions Card */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <h3 className="card-title">Order Actions</h3>
        </div>
        <div className="card-body">
          <ReactTooltip id="status-legend" style={{ maxWidth: '300px' }} />
          <ReactTooltip id="send-all-to-printer" style={{ maxWidth: '300px' }} />
          <ReactTooltip id="send-to-printer" style={{ maxWidth: '300px' }} />
          <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={updateStatus}>
              Update Status 
              <span 
                data-tooltip-id="status-legend" 
                data-tooltip-html="Status options:<br><ol><li>Order Placed: order_placed</li><li>Order Cancelled: cancelled</li><li>Sent to printer: sent_to_printer</li><li>Printed: printed</li><li>Awaiting delivery: awaiting_delivery</li><li>Delivered & Complete: complete</li></ol>" 
                className="info-icon" 
                style={{ marginLeft: 'var(--spacing-xs)' }}
              >
                ℹ️
              </span>
            </button>
            <button className="btn btn-danger" onClick={terminateOrder}>
              Cancel Order
            </button>
            <button className="btn btn-secondary" onClick={() => refund(order.cost.total)}>
              Full Refund
            </button>
            <button className="btn btn-secondary" onClick={() => refund()}>
              Partial Refund
            </button>
          </div>
        </div>
      </div>

      {/* Order Notes Card */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <h3 className="card-title">Order Notes</h3>
        </div>
        <div className="card-body">
          <div className="form-group">
            <textarea 
              className="form-textarea"
              placeholder="Add a note to this order"
              onChange={(e) => setOrder({ ...order, notes: e.target.value })}
              value={order.notes || ''}
            />
            <button 
              className="btn btn-primary" 
              onClick={() => {
                const note = order.notes || '';
                if (note.length > 500) {
                  alert("Note is too long. Please keep it under 500 characters.");
                  return;
                }
                updateOrderNote(note);
              }}
            >
              Save Note
            </button>
          </div>
        </div>
      </div>

      {/* Order Details Tabs */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
            <button 
              className={`btn ${tab === "cost" ? "btn-primary" : "btn-ghost"}`} 
              onClick={() => setTab("cost")}
            >
              Cost Breakdown
            </button>
            {(order.status === 'refunded' || order.status === 'partially_refunded' || order.status.includes("refunded") || order.refund) && (
              <button 
                className={`btn ${tab === "refund" ? "btn-primary" : "btn-ghost"}`} 
                onClick={() => setTab("refund")}
              >
                Refund Details
              </button>
            )}
            {order.discount && (
              <button 
                className={`btn ${tab === "discount" ? "btn-primary" : "btn-ghost"}`} 
                onClick={() => setTab("discount")}
              >
                Discount Details
              </button>
            )}
            <button 
              className={`btn ${tab === "billing" ? "btn-primary" : "btn-ghost"}`} 
              onClick={() => setTab("billing")}
            >
              Billing Details
            </button>
          </div>
        </div>
        <div className="card-body">
          {tab === "cost" && <OrderCost order={order} />}
          {tab === "discount" && order.discount && <OrderDiscount order={order} />}
          {tab === "refund" && (order.status === 'refunded' || order.status === 'partially_refunded' || order.status.includes("refunded") || order.refund) && (
            <RefundInfo order={order} handleEditShippingValue={handleEditShippingValue} />
          )}
          {tab === "billing" && <OrderBilling order={order} handleEditShippingValue={handleEditShippingValue} />}
                 </div>
      </div>

      {/* Items Ordered */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <h3 className="card-title">Items Ordered</h3>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-container" style={{ margin: 0, borderRadius: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Product ID</th>
                  <th>Product Name</th>
                  <th>Quantity</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((product, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'var(--font-family-mono)', fontWeight: 'var(--font-weight-medium)' }}>
                      {product.readable_id}
                    </td>
                    <td style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
                      {product.name}
                    </td>
                    <td>{product.quantity}</td>
                    <td style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)' }}>
                      ${toReadableTotal(product.price * product.quantity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>


      {/* Items Ordered */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <h3 className="card-title">Gift Specifications</h3>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-container" style={{ margin: 0, borderRadius: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Recipient Email</th>
                  <th>Personal Message</th>
                  <th>Sender Name</th>
                  <th>Promo Code</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((product, i) => (
                    <tr key={i}>
                        <td style={{ fontFamily: 'var(--font-family-mono)', fontWeight: 'var(--font-weight-medium)' }}>
                            {product.recipient || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>(empty)</span>}
                        </td>
                        <td style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
                            {product.personal_message || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>(empty)</span>}
                        </td>
                        <td>
                            {product.sender_name || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>(empty)</span>}
                        </td>
                        <td>
                            {order.promotion_code.code || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>(empty)</span>}
                        </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Order Logs */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Order Logs</h3>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {order.logs && order.logs.length > 0 ? (
            <div className="table-container" style={{ margin: 0, borderRadius: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Action</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {order.logs.map((log, index) => (
                    <tr key={index}>
                      <td style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)' }}>
                        {toReadableTimestamp(log.timestamp)}
                      </td>
                      <td style={{ fontWeight: 'var(--font-weight-medium)' }}>
                        {log.action}
                      </td>
                      <td>{log.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
              <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-lg)' }}>📋</div>
              <h3 style={{ marginBottom: 'var(--spacing-md)' }}>No Logs Available</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                No activity logs are available for this order.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GiftDetails;